import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { toast } from 'sonner';
// PostHog temporarily disabled
// import posthog from 'posthog-js';
const posthog = { identify: () => {}, reset: () => {} }; // stub

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasDoneOnboarding, setHasDoneOnboarding] = useState(false);
  const [hasLifetimeAccess, setHasLifetimeAccess] = useState(false);
  
  // Use same domain as frontend if backend URL not set (for Vercel serverless functions)
  const getBackendUrl = () => {
    if (process.env.REACT_APP_BACKEND_URL) {
      return process.env.REACT_APP_BACKEND_URL;
    }
    
    const isLocalhost = typeof window !== 'undefined' && 
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    
    if (isLocalhost) {
      return 'http://127.0.0.1:8000';
    }
    
    // If no backend URL set, use same domain (for Vercel serverless functions)
    if (typeof window !== 'undefined') {
      return window.location.origin;
    }
    return 'http://127.0.0.1:8000'; // Fallback for SSR
  };
  
  const BACKEND_URL = getBackendUrl();

  // Debug: Log backend URL on mount (only in production to help debug)
  const checkOnboardingStatusFromSupabase = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('has_lifetime_access, has_done_onboarding')
        .eq('id', userId)
        .single();
      if (!error && data) {
        setHasDoneOnboarding(data.has_done_onboarding);
        setHasLifetimeAccess(data.has_lifetime_access === true);
      }
    } catch {
      // Supabase fallback also failed — leave state as-is
    }
  };

  const checkOnboardingStatus = async (token, userId) => {
    // Skip if no token
    if (!token) {
      return;
    }

    // CRITICAL: Skip if backend URL is localhost and we're not on localhost (production)
    // This prevents "Failed to fetch" errors when env var is not set
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const isBackendLocalhost = BACKEND_URL.includes('localhost') || BACKEND_URL.includes('127.0.0.1');
    
    // If backend is localhost but we're in production, skip the check completely
    // This means REACT_APP_BACKEND_URL was not set or not picked up
    if (isBackendLocalhost && !isLocalhost) {
      // Silently skip - don't attempt fetch at all
      // The useEffect above already logged the error on mount
      return;
    }

    // Double-check: if BACKEND_URL is still the default, skip in production
    if ((BACKEND_URL === 'http://127.0.0.1:8000' || BACKEND_URL === 'http://127.0.0.1:8000') && !isLocalhost) {
      return;
    }

    try {
        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
        
        const response = await fetch(`${BACKEND_URL}/api/user/status`, {
            headers: {
                Authorization: `Bearer ${token}`
            },
            signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
            const data = await response.json();
            setHasDoneOnboarding(data.has_done_onboarding);
          setHasLifetimeAccess(data.has_lifetime_access === true);
        } else {
            // Non-200 response - log but don't throw
            console.warn(`Onboarding status check returned ${response.status}`);
        }
    } catch (e) {
        // Network errors, timeouts, etc. - handle gracefully
        // Only log as warning, not error, to reduce noise
        if (e.name === 'AbortError' || e.name === 'TimeoutError') {
            // Silently handle timeout - backend might be slow
            return;
        } else if (e.message?.includes('Failed to fetch') || e.message?.includes('NetworkError') || e.message?.includes('CORS')) {
            // Backend unreachable (ad blocker, network issue, etc.)
            // Fall back to querying Supabase directly so existing users aren't hit with paywall
            if (userId) {
              await checkOnboardingStatusFromSupabase(userId);
            }
            return;
        } else {
            // Other errors - log but don't break
            console.warn("⚠️ Onboarding status check failed:", e.message || e);
        }
        // Don't set hasDoneOnboarding to false on error - keep current state
    }
  };

  useEffect(() => {
    // Check active session
    const initAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error("Error recovering session:", error);
          setUser(null);
          setLoading(false);
          return;
        }

        setUser(session?.user ?? null);
        if (session?.user) {
          // PostHog identify - non-blocking, wrapped in try-catch
          try {
            posthog.identify(session.user.id, { email: session.user.email });
          } catch (e) {
            // Silently ignore PostHog errors - analytics shouldn't block auth
          }
          // Wait for onboarding status before setting loading to false
          await checkOnboardingStatus(session.access_token, session.user.id);
        }
        setLoading(false);
      } catch (err) {
        console.error("Unexpected error checking session:", err);
        setUser(null);
        setLoading(false);
      }
    };

    initAuth();

    // Listen for auth changes (e.g., after Google OAuth redirect)
    // Use a flag to prevent duplicate processing
    let isProcessing = false;
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      // Prevent concurrent processing which can cause freeze
      if (isProcessing) return;
      isProcessing = true;

      try {
        setUser(session?.user ?? null);
        if (session?.user) {
          // PostHog identify - non-blocking
          try {
            posthog.identify(session.user.id, { email: session.user.email });
          } catch (e) {}
          await checkOnboardingStatus(session.access_token, session.user.id);
        } else {
          try {
            posthog.reset();
          } catch (e) {}
          setHasDoneOnboarding(false);
          setHasLifetimeAccess(false);
        }
      } finally {
        isProcessing = false;
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });
      
      if (error) throw error;
      
      toast.success('Account created! Please check your email to verify.');
      return { data, error: null };
    } catch (error) {
      toast.error(error.message);
      return { data: null, error };
    }
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      
      toast.success('Welcome back!');
      return { data, error: null };
    } catch (error) {
      toast.error(error.message);
      return { data: null, error };
    }
  };

  const signInWithGoogle = async () => {
    try {
      // Use the full URL including pathname to ensure proper redirect
      const redirectTo = `${window.location.origin}${window.location.pathname}`;

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectTo,
          queryParams: {
            prompt: 'select_account',  // Force Google to show account picker
          },
        },
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      toast.error(error.message);
      return { data: null, error };
    }
  };

  const signInWithMagicLink = async (email, captchaToken = null) => {
    try {
      const options = {
        emailRedirectTo: window.location.origin,
      };

      // Add captcha token if provided (for Cloudflare Turnstile)
      if (captchaToken) {
        options.captchaToken = captchaToken;
      }

      const { data, error } = await supabase.auth.signInWithOtp({
        email,
        options,
      });

      if (error) throw error;

      // toast.success('Check your email for the login link!');
      return { data, error: null };
    } catch (error) {
      toast.error(error.message || 'Error sending magic link');
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      toast.success('Signed out successfully');
      return { error: null };
    } catch (error) {
      toast.error(error.message);
      return { error };
    }
  };

  const value = {
    signUp,
    signIn: async (email, password) => {
      try {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
        return { data, error: null };
      } catch (error) {
        toast.error(error.message);
        return { data: null, error };
      }
    },
    signOut: async () => {
      try {
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
      } catch (error) {
        toast.error(error.message);
      }
    },
    signInWithGoogle,
    signInWithMagicLink,
    user,
    hasDoneOnboarding,
    hasLifetimeAccess,
    grantLifetimeAccess: () => setHasLifetimeAccess(true),
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
